import { Box, Grid, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import styles from './DailyActivities.module.scss';
import DailyActivitiesForm from './DilayActivitiesForm';
import { object, string, ValidationError, number } from 'yup';
import {
   CommonButton,
   CommonOutlinedButton,
   CustomCalendarTodayOutlinedIcon,
   CustomDatePicker,
   CustomInputLabel,
   CustomRequiredInputLabel,
   StyledDateContainer,
} from '../../common/StyledComponents/StyledFields';
import DailyActivityInformation from './DailyActivityInformation';
import {
   apiRequest,
   catchErrorMessageHandler,
   displayDateFormat,
} from '../../../infrastructure/backendService';
import apiPath from '../../../config/apiPaths.json';
import { ClipLoader } from 'react-spinners';

interface ErrorMessageTypes {
   fromDate: string;
   toDate: string;
}
const DailyActivities = (): JSX.Element => {
   const formFields = {
      fromDate: '',
      toDate: '',
   };
   const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
      ...formFields,
   });
   const [showForm, setShowForm] = useState(false);
   const [isEditFormOpen, setIsEditFormOpen] = useState(false);
   const [pageNumber, setPageNumber] = useState<number>(1);
   const [totalRecords, setTotalRecords] = useState<number>(0);
   const [formData, setFormData] = useState({
      ...formFields,
   });
   const [submitLoading, setSubmitLoading] = useState(false);
   const [activityTableList, setActivityTableList] = useState([]);
   const [activityTable, setActivityTable] = useState(false);
   const [showMessage, setShowMessage] = useState(false);

   const validationSchema = object().shape({
      fromDate: string().required('From Date is required'),
      toDate: string()
         .required('To Date is required')
         .test('is-after-from-date', 'To Date cannot be before From Date', function (value) {
            const { fromDate } = this.parent;
            if (!fromDate || !value) return true; // if either date is not provided, let Yup handle the "required" validation
            return new Date(value) >= new Date(fromDate);
         }),
   });
   const handleFieldChange = (field: string, value: any) => {
      if (field === 'fromDate' || field === 'toDate') {
         const formattedDate = dayjs(value, 'MM-DD-YYYY');
         setFormData({
            ...formData,
            [field]: formattedDate.isValid() ? formattedDate.format('MM-DD-YYYY') : '',
         });
      } else {
         setFormData({
            ...formData,
            [field]: value,
         });
      }
   };
   const handleAddActivity = (e: any) => {
      e.preventDefault();
      setShowForm(true);
      setActivityTable(false);
   };
   const handleBackAdd = () => {
      setShowForm(false);
      searchApiCall();
      setActivityTable(true);
   };

   const handleSearchClick = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields };
      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         searchApiCall();
         setActivityTable(true);
      } catch (error: any) {
         // Handle validation
         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };

   useEffect(() => {
      searchApiCall();
   }, [pageNumber]);

   const searchApiCall = () => {
      if (formData.fromDate && formData.toDate) {
         setSubmitLoading(true);
         apiRequest({
            method: 'GET',
            endpoint: apiPath.addedTempleActivity,
            includeToken: true,
            params: {
               startDate: displayDateFormat(new Date(formData.fromDate)),
               endDate: displayDateFormat(new Date(formData.toDate)),
               page: pageNumber - 1,
               size: 20,
               direction: 'asc',
            },
         })
            .then((res) => {
               setTotalRecords(res.data.totalElements);
               setActivityTableList(res.data.content);
               setSubmitLoading(false);
               setShowMessage(true);

            })
            .catch((err) => {
               console.log(err, 'error');
               setSubmitLoading(false);
            });
      }
   };
   const FromDate = (): React.ReactElement => {
      return (
         <div style={{ minWidth: '100%', marginTop: '12px' }}>
            <StyledDateContainer sx={{ borderRadius: '8px', background: '#FFF9E4' }}>
               <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DateTimePicker']}>
                     <CustomDatePicker
                        slots={{
                           openPickerIcon: CustomCalendarTodayOutlinedIcon,
                        }}
                        slotProps={{
                           inputAdornment: {
                              position: 'end',
                           },
                        }}
                        format="MM-DD-YYYY"
                        value={formData.fromDate ? dayjs(formData.fromDate, 'MM-DD-YYYY') : ''}
                        onChange={(value: any) => {
                           setErrorMessage({ ...errorMessage, fromDate: '' });
                           handleFieldChange('fromDate', value);
                        }}
                        className={errorMessage.fromDate ? styles.errorTextField : styles.textField}
                     />
                  </DemoContainer>
               </LocalizationProvider>
            </StyledDateContainer>
            {errorMessage.fromDate ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.fromDate}
               </Typography>
            ) : (
               ''
            )}
         </div>
      );
   };
   const ToDate = (): React.ReactElement => {
      return (
         <div style={{ minWidth: '100%', marginTop: '12px' }}>
            <StyledDateContainer sx={{ borderRadius: '8px', background: '#FFF9E4' }}>
               <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DateTimePicker']}>
                     <CustomDatePicker
                        slots={{
                           openPickerIcon: CustomCalendarTodayOutlinedIcon,
                        }}
                        slotProps={{
                           inputAdornment: {
                              position: 'end',
                           },
                        }}
                        format="MM-DD-YYYY"
                        value={formData.toDate ? dayjs(formData.toDate, 'MM-DD-YYYY') : ''}
                        onChange={(value) => {
                           setErrorMessage({ ...errorMessage, toDate: '' });
                           handleFieldChange('toDate', value);
                        }}
                        className={errorMessage.toDate ? styles.errorTextField : styles.textField}
                     />
                  </DemoContainer>
               </LocalizationProvider>
            </StyledDateContainer>
            {errorMessage.toDate ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.toDate}
               </Typography>
            ) : (
               ''
            )}
         </div>
      );
   };

   const handleKeyPress = (e: any) => {
      if (e.key === 'Enter') {
         e.preventDefault(); // Prevent the default form submission behavior
         handleSearchClick();
      }
   };
   return (
      <Box className={styles.totalLayout}>
         {!showForm ? (
            <>
               {!isEditFormOpen && (
                  <div>
                     <Grid>
                        <Typography className={styles.SubHeading}>Daily Activities</Typography>
                     </Grid>
                     <form className={styles.formLayout} onKeyDown={handleKeyPress}>
                        <Grid container spacing={2}>
                           <Grid xs={12} sm={4} item>
                              {CustomRequiredInputLabel('From Date', true)}
                              {FromDate()}
                           </Grid>
                           <Grid xs={12} sm={4} item>
                              {CustomRequiredInputLabel('To Date', true)}
                              {ToDate()}
                           </Grid>
                        </Grid>
                     </form>
                     <CommonButton onClick={handleSearchClick}>
                        {submitLoading ? (
                           <>
                              <ClipLoader size={20} color="#fff" />
                              <span style={{ marginLeft: '8px' }}>Search</span>
                           </>
                        ) : (
                           'Search'
                        )}
                     </CommonButton>
                     <CommonOutlinedButton
                        onClick={(e: any) => handleAddActivity(e)}
                        style={{ marginLeft: '15px' }}
                     >
                        Add New
                     </CommonOutlinedButton>
                  </div>
               )}
               {activityTable && (
                  <DailyActivityInformation
                     activityTableList={activityTableList}
                     setActivityTableList={setActivityTableList}
                     totalRecords={totalRecords}
                     setPageNumber={setPageNumber}
                     pageNumber={pageNumber}
                     setIsEditFormOpen={setIsEditFormOpen}
                     showMessage={showMessage}
                  />
               )}
            </>
         ) : (
            <DailyActivitiesForm handleBack={handleBackAdd} />
         )}
      </Box>
   );
};

export default DailyActivities;
